import { useT } from "@/lib/i18n-dict";
import { localeMeta } from "@/lib/seo-head";
import { useTx } from "@/lib/i18n-dict";
import { createFileRoute } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Section, SectionHeading } from "@/components/site/Section";
import { GhostCta, PrimaryCta } from "@/components/site/Cta";
import { LocaleLink } from "@/components/site/LocaleLink";
import { RequestForm } from "@/components/site/RequestForm";
import { CONTACTS as CONTACTS_SOURCE } from "@/content/pages";
import { alternateLinks } from "@/lib/i18n";

const TITLE = "Contacts — talk to the factory.online team";
const DESCRIPTION =
  "Reach the factory.online team for sales, tailored demos, partnerships or support. Email office@factory.online to discuss your production needs.";

export const Route = createFileRoute("/{-$locale}/contacts")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return ({
    meta: [
      { title: __m.title },
      { name: "description", content: __m.description },
      { property: "og:title", content: __m.title },
      { property: "og:description", content: __m.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: alternateLinks("/contacts"),
  });
  },
  component: Contacts,
});

function Contacts() {
  const CONTACTS = useTx(CONTACTS_SOURCE);
  const t = useT();
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <div className="container-page relative py-20 md:py-28">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {CONTACTS.intro.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl">
              {CONTACTS.intro.title}
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">
              {CONTACTS.intro.lead}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={`mailto:${CONTACTS.email}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              >
                <Mail className="size-4" aria-hidden />
                {CONTACTS.email}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
              <GhostCta to="/contacts">Contact us</GhostCta>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Reasons to reach out */}
      <Section>
        <SectionHeading eyebrow="How can we help" title="Have an idea or a question? Let's discuss" />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {CONTACTS.reasons.map((reason, index) => (
            <Reveal
              key={reason.title}
              delay={index * 50}
              className="rounded-xl border border-border bg-surface/40 p-6"
            >
              <h3 className="font-display text-base font-semibold">{reason.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{reason.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Offices */}
      <Section className="bg-surface/40">
        <SectionHeading eyebrow="Where we are" title="Our office" />
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {CONTACTS.offices.map((office, index) => (
            <Reveal
              key={office.name}
              delay={index * 50}
              className="rounded-xl border border-border p-6"
            >
              <h3 className="font-display text-base font-semibold">{office.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{office.address}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Contact form */}
      <Section>
        <div className="mx-auto max-w-3xl">
          <SectionHeading eyebrow="Write to us" title="Tell us about your production" />
          <div className="mt-10">
            <RequestForm
              kind="contact"
              withMessage
              submitLabel="Send a message"
              successTitle="Message received"
              successText="Thank you — our team will get back to you within one business day."
            />
          </div>
        </div>
      </Section>

      {/* Closing CTA */}
      <Section>
        <Reveal className="rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {t("Ready to see factory.online on your own data?")}
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            {t("See the workflows that matter to your plant in a tailored session.")}{" "}
            <LocaleLink
              to="/registration-demo"
              className="font-semibold text-primary hover:underline"
            >
              {t("Book a demo")}
            </LocaleLink>
            .
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
            <GhostCta to="/contacts">Contact us</GhostCta>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
